// Structured error context must be safe to hand to a publisher, to
// Diagnostics, and to an exported JSON support bundle. This is the one gate
// that guarantees it (Milestone 6.10/ADR-026).
//
// Three rules, in order of importance:
//
//  1. NEVER carry a secret. Any key whose name looks like a credential is
//     dropped outright rather than redacted-in-place, so it cannot be
//     reconstructed from the shape of the object either.
//  2. NEVER retain a large object. DOM nodes, media elements, Response
//     objects, functions and deep structures are dropped - keeping one alive
//     inside a long-lived Diagnostics error history would leak memory as well
//     as data. Strings are truncated.
//  3. NEVER throw. Sanitizing is something that happens while an error is
//     already being constructed; a failure here would replace a real error
//     with a confusing one.
const SENSITIVE_KEY =
  /auth|cookie|token|credential|password|secret|session[-_]?key|api[-_]?key|bearer/i;

const MAX_STRING_LENGTH = 512;
const MAX_KEYS = 24;

export function sanitizeContext(context) {
  if (context === null || typeof context !== 'object' || Array.isArray(context)) {
    return Object.freeze({});
  }

  const result = {};
  let count = 0;

  for (const [key, value] of Object.entries(context)) {
    if (count >= MAX_KEYS) {
      break;
    }
    if (SENSITIVE_KEY.test(key)) {
      continue;
    }

    const safe = sanitizeValue(value, 0);
    if (safe !== undefined) {
      result[key] = safe;
      count += 1;
    }
  }

  return Object.freeze(result);
}

// Depth 1 is as deep as context is allowed to go. Anything nested further is
// dropped rather than flattened: an error context is a debugging aid, not a
// serialization format, and "one level of plain data" has been enough for
// every call site in the SDK.
function sanitizeValue(value, depth) {
  if (value === null) {
    return null;
  }

  const type = typeof value;

  if (type === 'string') {
    return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value;
  }
  if (type === 'number' || type === 'boolean') {
    return Number.isFinite(value) || type === 'boolean' ? value : undefined;
  }
  if (type === 'bigint') {
    return value.toString();
  }
  if (type === 'function' || type === 'symbol' || type === 'undefined') {
    return undefined;
  }

  // Anything with a DOM/media/network shape is dropped by identity rather
  // than by duck-typing every possible class.
  if (isUnsafeObject(value)) {
    return undefined;
  }

  if (depth >= 1) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_KEYS)
      .map((item) => sanitizeValue(item, depth + 1))
      .filter((item) => item !== undefined);
    return Object.freeze(items);
  }

  const nested = {};
  let count = 0;
  for (const [key, item] of Object.entries(value)) {
    if (count >= MAX_KEYS) {
      break;
    }
    if (SENSITIVE_KEY.test(key)) {
      continue;
    }
    const safe = sanitizeValue(item, depth + 1);
    if (safe !== undefined) {
      nested[key] = safe;
      count += 1;
    }
  }
  return Object.freeze(nested);
}

function isUnsafeObject(value) {
  if (typeof Node !== 'undefined' && value instanceof Node) {
    return true;
  }
  if (typeof Response !== 'undefined' && value instanceof Response) {
    return true;
  }
  if (typeof Request !== 'undefined' && value instanceof Request) {
    return true;
  }
  if (typeof Headers !== 'undefined' && value instanceof Headers) {
    return true;
  }
  // An Error nested inside context would duplicate the cause chain and can
  // carry a stack; causes belong on `error.cause`, not in context.
  return value instanceof Error;
}
